import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useMemo } from 'react'
import { ContentItemViewNodesType, DataNode } from '../types'
import { useShallowSelector, useWorkspace2Store } from '../Workspace2Store'
import { useNodeMapShallowSelector } from './keyOperations'

// Storage keys for auto-expansion functionality
const STORAGE_KEYS = {
    PENDING_EXPAND: 'pendingExpandNewArticle',
    USER_ARTICLES_BEFORE_SAVE: 'userArticlesBeforeSave',
} as const

/**
 * Hook that manages auto-expansion of newly created articles * 
 * @returns Object containing functions for managing auto-expansion
 */
export const useAutoExpandNewArticle = () => {
    /**
     * Prepare for auto-expansion by storing current article IDs
     * Call this before the save operation
     */
    const prepareForAutoExpand = useCallback(
        (options: {
            contentItemViewNodes: Partial<ContentItemViewNodesType>[]
            isNodeToAddArticle: boolean
        }) => {
            if (!options.isNodeToAddArticle) return

            // Store current user-added article IDs for comparison after save
            const currentUserArticleIds = options.contentItemViewNodes
                .filter(node => node.textType === 'Article' && node.isUserAdded && node.id)
                .map(node => node.id as string)
            
            localStorage.setItem(STORAGE_KEYS.USER_ARTICLES_BEFORE_SAVE, JSON.stringify(currentUserArticleIds))
            localStorage.setItem(STORAGE_KEYS.PENDING_EXPAND, 'true')
        },
        []
    )

    /**
     * Handle auto-expansion after content has been saved
     * This identifies the new article and adds it to the loading state
     */
    const handleAutoExpandAfterSave = useCallback(
        (optimisticTreeViewNodes: Partial<ContentItemViewNodesType>[]) => {
            // Check if auto-expansion is pending
            if (localStorage.getItem(STORAGE_KEYS.PENDING_EXPAND) !== 'true') return

            // Get the article IDs from before the save
            let previousArticleIds: string[] = []
            try {
                const stored = localStorage.getItem(STORAGE_KEYS.USER_ARTICLES_BEFORE_SAVE)
                previousArticleIds = stored ? JSON.parse(stored) : []
            } catch {
                previousArticleIds = []
            }

            // Find the newly added article
            const currentUserArticles = optimisticTreeViewNodes.filter(
                node => node.textType === 'Article' && node.isUserAdded
            )
            const newArticle = currentUserArticles.find(
                article => article.id && !previousArticleIds.includes(article.id)
            )

            if (newArticle?.id) {
                // Add the new article to articlesLoading for auto-expansion
                const currentState = useWorkspace2Store.getState()
                const updatedArticlesLoading = [...currentState.articlesLoading]
                
                if (!updatedArticlesLoading.includes(newArticle.id)) {
                    updatedArticlesLoading.push(newArticle.id)
                    currentState.actions.setArticlesLoading(updatedArticlesLoading)
                }
            }

            // Clean up storage after processing
            localStorage.removeItem(STORAGE_KEYS.PENDING_EXPAND)
            localStorage.removeItem(STORAGE_KEYS.USER_ARTICLES_BEFORE_SAVE)
        },
        []
    )

    /**
     * Clean up any pending auto-expansion state
     * Call this on error or when canceling the operation
     */
    const cleanupAutoExpand = useCallback(() => {
        localStorage.removeItem(STORAGE_KEYS.PENDING_EXPAND)
        localStorage.removeItem(STORAGE_KEYS.USER_ARTICLES_BEFORE_SAVE)
    }, [])

    return {
        prepareForAutoExpand,
        handleAutoExpandAfterSave,
        cleanupAutoExpand,
    }
}

/**
 * Hook that determines if a specific article node is currently being added
 * @param node - The tree node to check
 * @returns Boolean indicating if this article is being added
 */
export const useIsArticleBeingAdded = (node: DataNode): boolean => {
    const { textContentElementToAdd } = useShallowSelector((state) => ({
        textContentElementToAdd: state.textContentElementToAdd,
    }))

    const {
        isSaveOperationInProgress,
        isElementBeingAdded,
        isElementRecentlyAdded,
    } = useNodeMapShallowSelector((state) => ({
        isSaveOperationInProgress: state.isSaveOperationInProgress,
        isElementBeingAdded: state.isElementBeingAdded,
        isElementRecentlyAdded: state.isElementRecentlyAdded,
    }))

    // Check if content is being added (same logic as AddIcon)
    const hasContentBeingAdded = !!textContentElementToAdd || isSaveOperationInProgress

    // Check if this specific article is the one being added
    const isThisArticleBeingAdded = useMemo(() => {
        // Only check articles
        if (node.type !== NODE_TYPES.Article) return false

        // Check if this is a newly created article with empty content
        if (hasContentBeingAdded && node.isUserAdded && node.contentItemNodes?.[0]) {
            const firstElement = node.contentItemNodes[0]
            if (firstElement.changeType === 'ADDED' && !firstElement.text) {
                return true
            }
        }

        // Check if this article contains the element being added
        if (textContentElementToAdd && node.contentItemNodes) {
            const hasTargetElement = node.contentItemNodes.some(
                (element) => element?.id === textContentElementToAdd.id
            )
            if (hasTargetElement) return true
        }

        // Check if any elements in this article are being processed
        if (node.contentItemNodes) {
            const hasProcessingElement = node.contentItemNodes.some(
                (element) =>
                    element?.id &&
                    (isElementBeingAdded(element.id) ||
                        isElementRecentlyAdded(element.id))
            )
            if (hasProcessingElement) return true
        }

        return false
    }, [
        node,
        textContentElementToAdd,
        hasContentBeingAdded,
        isElementBeingAdded,
        isElementRecentlyAdded,
    ])

    return isThisArticleBeingAdded
}